import { useCallback, useState } from 'react';

import { GripVertical, Info, RefreshCcwDot, Trash } from 'lucide-react';
import moment from 'moment-timezone';
import { Select, SimpleAlert, Badge, Button, Col, Label, Row, DefaultPopover, DefaultTooltip, Separator, FormGroup, ButtonIcon } from 'twyne-new.ui';

import { ControlledConfirmationPopover } from '@/components/ConfirmationPopover';
import { useSortableDndListItem } from '@/components/SortableDnd';
import DateTimePicker from '@Components/DateTimePicker';
import InfoTip from '@Components/InfoTip';
import { PureSwitch } from '@Components/form/Switch';
import app from '@Stores/app';

import Versions from './Versions';

const METRICS_OPTIONS = [
  { value: 'completed', label: 'Page Completed' },
  { value: 'rpu', label: 'RPU' },
  { value: 'revenue', label: 'Revenue' },
]

export default function Header({ sortableId, index, slot, onChangeSlot, onRemoveSlot, originalSlotsPositions }) {
  return <>
    <header className="flex items-center justify-between twy-subtle-neutral py-2 pl-2 pr-4">
      <TitleSlot sortableId={sortableId} index={index} slot={slot} originalSlotsPositions={originalSlotsPositions} />
      <div className='flex items-center gap-2'>
        {slot.pages.length > 1 && <>
          <ExperimentInfos slot={slot} index={index} />
          <Optimize data={slot.optimize} onChangeOptimize={optimize => onChangeSlot({ data: optimize, key: "optimize", slotIndex: index })} />
        </>}
        <Actions index={index} slot={slot} onChangeSlot={onChangeSlot} onRemoveSlot={onRemoveSlot} />
      </div>
    </header>
  </>
}

function Optimize({ data, onChangeOptimize }) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [experiment, setExperiment] = useState(data?.experiment || false)
  const [metric, setMetric] = useState(data?.evaluateMetric || 'weight')
  const [winnerMetric, setWinnerMetric] = useState(data?.winnerMetric)
  const [expirationDate, setExpirationDate] = useState(data?.expirationDate && moment(data.expirationDate))
  const [overrideTrigger, setOverrideTrigger] = useState(false)

  const handleToggle = useCallback(() => {
    if (overrideTrigger) {
      setOverrideTrigger(false)
      return;
    }
    setPopoverOpen(prev => !prev);
  }, [overrideTrigger])

  const handleSave = () => {
    if (experiment && (!expirationDate || !winnerMetric)) {
      setOverrideTrigger(true)
      setPopoverOpen(false)
      return
    }
    onChangeOptimize({ evaluateMetric: metric, experiment, winnerMetric: !experiment ? null : winnerMetric, expirationDate: !experiment ? null : expirationDate.utc().endOf('day') })
    setOverrideTrigger(true)
    setPopoverOpen(false)
  }

  const handleCancel = () => {
    setOverrideTrigger(true)
    setPopoverOpen(false)
  }

  const isOutsideRange = day => day.isBefore(moment(), 'day')

  return <>
    <DefaultPopover
      contentProps={{ side: 'left' }}
      wrapperProps={{ open: popoverOpen, onOpenChange: handleToggle }}
      trigger={<Button size="sm" color='accent' variant='ghost' > optimize </Button>}>
      <Row>
        <Col size="12">
          <FormGroup>
            <Label className="flex gap-1">
              Evaluate Metric
              <InfoTip id="metric-tip">
                This value will be used to determine the page rendering.
              </InfoTip>
            </Label>
            <Select
              value={metric}
              options={[{ value: 'weight', label: 'Weight' }]}
              onChange={option => setMetric(option.value)}
              clearable={false}
              disabled
            />
          </FormGroup>
        </Col>
        <Col size="5">
          <FormGroup>
            <Label>Experiment</Label>
            <PureSwitch onChange={event => setExperiment(event.target.checked)} value={experiment} required />
          </FormGroup>
        </Col>

        {experiment && <>
          <Col size='12'>
            <FormGroup>
              <Label>Expiration Date</Label>
              <DateTimePicker
                meridiemSystem
                value={expirationDate}
                onChange={date => setExpirationDate(date)}
                openDirection="down"
                isDayBlocked={() => false}
                isOutsideRange={isOutsideRange}
                mask='MM/DD/YYYY'
                required
                closeOnSelect
              />
            </FormGroup>
          </Col>
          <Col size="12">
            <FormGroup>
              <Label className="flex gap-1">
                Winner Metric
                <InfoTip id="metric-winnr-tip">
                  This value will be used to determine the page winner in the expiration date.
                </InfoTip>
              </Label>
              <Select
                value={winnerMetric}
                options={METRICS_OPTIONS}
                onChange={option => setWinnerMetric(option.value)}
                clearable={false}
                required
              />
            </FormGroup>
          </Col>
        </>}
        <Col>
          <div className='flex gap-2'>
            <Button color="accent" onClick={handleSave}>Apply</Button>
            <Button variant='ghost' onClick={handleCancel}>Cancel</Button>
          </div>
        </Col>
      </Row>
    </DefaultPopover>
  </>
}

function Actions({ index, slot, onChangeSlot, onRemoveSlot }) {
  return <>
    {(slot.id && slot.optimize?.experiment) && <>
      <ControlledConfirmationPopover
        inline
        target={<ButtonIcon content='Reset experiment'> <RefreshCcwDot size={15} /> </ButtonIcon>}
        title={`Reset slot #${index + 1}`}
        id={`reset-${index}`}
        onSubmit={() => onChangeSlot({ data: !slot.isManualReset, key: "isManualReset", slotIndex: index })}
      >
        {!slot.isReset ? <p><strong>This action will reset experiment, are you sure you want do this?</strong></p> : <p><strong>Cancel reset slot.</strong></p>}
      </ControlledConfirmationPopover>
      <Separator orientation='vertical' className='py-2 twy-border-neutral' />
    </>}
    {slot.id && <>
      <Versions slotId={slot.id} />
      <Separator orientation='vertical' className='py-2 twy-border-neutral' />
    </>}
    <ControlledConfirmationPopover
      inline
      target={<ButtonIcon content='Delete slot'> <Trash size={16} /> </ButtonIcon>}
      title={`Delete slot #${index + 1}`}
      id={`delete-${index}`}
      onSubmit={() => onRemoveSlot(index)}
    >
      <p><strong>Are you sure you want do this?</strong></p>
    </ControlledConfirmationPopover>
  </>
}

function TitleSlot({ index, slot, originalSlotsPositions, sortableId }) {
  const hasOriginalPosition = originalSlotsPositions ?? originalSlotsPositions[slot.id]
  const hasExperiment = !!slot.optimize?.experiment
  const shouldShowAlert = hasExperiment && ((hasOriginalPosition && originalSlotsPositions[slot.id] !== index) || slot.isReset || slot.isManualReset)
  const { dragProps } = useSortableDndListItem(sortableId)

  return <div className="flex items-center gap-2 px-2">
    <GripVertical size='16' {...dragProps} />
    <div className='flex items-center gap-1'>
      <span>Container slot</span>
      <span className="twy-text-base-accent">
        #{index + 1}
      </span>
    </div>
    {shouldShowAlert && <SimpleAlert variant="warning" className='px-2 py-0' >This slot has actions that will reset the optimize.</SimpleAlert>}
  </div>
}

function ExperimentInfos({ slot }) {
  const hasDates = slot.optimize?.expirationDate && slot.optimize?.startedDate

  const runFor = hasDates && moment.duration(moment.tz(app.timezone).diff(slot.optimize.startedDate), 'ms').humanize()
  const daysLeft = hasDates && moment.duration(moment(slot.optimize.expirationDate).tz(app.timezone).diff(moment.tz(app.timezone).add()), 'ms').humanize()

  return <>
    {hasDates && <div className="flex flex-col items-end" >
      <small className='flex items-center gap-1'>
        <span>
          running for {runFor}
        </span>
        <DefaultTooltip content={`started at: ${moment(new Date()).tz(app.timezone).format('DD/MM/YYYY HH:mm:ss')}`}>
          <Info size='12' className='twy-text-base-accent' />
        </DefaultTooltip>
      </small>
      <small className='flex items-center gap-1'>
        <span>
          {daysLeft} remaining
        </span>
        <DefaultTooltip content={`ends at: ${moment(new Date()).tz(app.timezone).format('DD/MM/YYYY HH:mm:ss')}`}>
          <Info size='12' className='twy-text-base-accent' />
        </DefaultTooltip>
      </small>
    </div>}
    <Separator orientation='vertical' className='py-2 twy-border-neutral' />
    <Badge className='self-center!' variant='outline' size='sm' color={slot.optimize?.experiment ? 'success' : 'danger'}>{slot.optimize?.experiment ? 'live' : 'off'}</Badge>
    <Separator orientation='vertical' className='py-2 twy-border-neutral' />
  </>
}
